import _ from 'lodash';
import { useSelector } from 'react-redux';
import { ForgeIcon } from '@tylertech/forge-react';
import { FeatureFlags } from '@socrata/platform/feature-flags';

import { type FilterParameterConfiguration } from 'common/types/reportFilters';
import {
  getDataSourceMetadata,
  getFilterParameterConfigurations,
  hasEmptyDataSource,
} from 'store/selectors/DataSourceSelectors';
import { type ReactRendererProps } from '../../shared/ComponentRegistry';
import StorytellerUtils from 'lib/StorytellerUtils';
import { FilterBarContainer } from './filter-bar-container';
import { SelectDatasetButton } from '../../componentGlobalFilter/SelectDatasetButton';

export const ComponentGlobalFilter = ({
  blockId,
  componentIndex,
  editMode,
  componentData,
}: ReactRendererProps) => {
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(componentData.type);
  const metadata = useSelector(getDataSourceMetadata, _.isEqual);
  const datasourceEmpty = useSelector(hasEmptyDataSource, _.isEqual);
  const parameterFilterConfigurations = useSelector(getFilterParameterConfigurations, _.isEqual);

  const canBatchRun = FeatureFlags.valueOrDefault('enable_batch_running_filters');
  // The expandable global filter bar requires both enable_batch_running_filters and enable_updated_gfb.
  const useExpandableGfb = canBatchRun && FeatureFlags.valueOrDefault('enable_updated_gfb');

  const classes =
    'global-filter-actions-container global-filter-actions-container-forge-viz-style full-width-content';

  const globalFilterClasses = `global-filter background-filter-bar${useExpandableGfb ? ' gfb-expandable' : ''}`;

  const noVisibleGlobalConfigurations: boolean =
    _.isEmpty(parameterFilterConfigurations) ||
    _.every(parameterFilterConfigurations, ({ config }: FilterParameterConfiguration) => config.isHidden);

  if (!editMode && (noVisibleGlobalConfigurations || datasourceEmpty)) {
    // Global Filter Bar should be hidden in view mode when there are no visible filters or parameters
    return null;
  }

  if (!datasourceEmpty && _.isEmpty(metadata)) {
    // If this is configured, but metadata hasn't loaded yet, show some loading state
    return (
      <div className={`component ${typeClasses}`}>
        <div className={globalFilterClasses}>
          <div className={classes}></div>
        </div>
      </div>
    );
  }

  if (datasourceEmpty) {
    return (
      <div className={`component ${typeClasses}`}>
        <div className={globalFilterClasses}>
          <div className={`${classes} global-filter-unconfigured`}>
            <div className="select-dataset-container">
              <SelectDatasetButton blockId={blockId} componentIndex={componentIndex} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`component ${typeClasses}`}>
      <div className={globalFilterClasses}>
        <div className={classes}>
          {!useExpandableGfb && (!canBatchRun || editMode) && (
            <ForgeIcon name="filter_list" className="icon-1x global-filter-icon" />
          )}
          <div className="horizontal-filter-bar">
            <FilterBarContainer editMode={editMode} />
          </div>
        </div>
      </div>
    </div>
  );
};
